// RechargeModal - 积分包与月订阅支付弹窗
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    X,
    Zap,
    Loader2,
    CheckCircle2,
    ScanLine,
    Gem,
    CalendarClock,
    AlertCircle,
    Crown,
} from 'lucide-react';

const FALLBACK_PACKAGE_PLANS = [];

const isPaidStatus = (value) => {
    const status = String(value || '').toUpperCase();
    return status === 'SUCCESS' || status === 'PAID';
};

const toQrImageUrl = (codeUrl) => {
    if (!codeUrl) return '';
    const normalized = String(codeUrl);
    if (/\.(png|jpg|jpeg|webp|gif)(\?|$)/i.test(normalized)) {
        return normalized;
    }
    return `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(normalized)}`;
};

const pickMonthlyPlan = (plans) => {
    if (!Array.isArray(plans) || plans.length === 0) return null;
    const byDays = plans.find((item) => Number(item?.cycle_days) === 30);
    if (byDays) return byDays;
    const byName = plans.find((item) => /月/.test(String(item?.name || '')));
    return byName || plans[0];
};

const planPrice = (plan) => Number(plan?.price || 0).toFixed(2);
const planSubtitle = (plan) => `开通后立即到账 ${plan?.points_per_cycle || 0} 积分，后续每 ${plan?.cycle_days || 30} 天发放`;

const RechargeModal = ({ onClose, authToken, API_BASE_URL, onPaymentSuccess }) => {
    const [activeType, setActiveType] = useState('package');
    const [selectedPackageId, setSelectedPackageId] = useState(null);

    const [qrCodeUrl, setQrCodeUrl] = useState('');
    const [loadingQr, setLoadingQr] = useState(false);
    const [orderId, setOrderId] = useState('');
    const [outTradeNo, setOutTradeNo] = useState('');
    const [payStatus, setPayStatus] = useState('pending');
    const [error, setError] = useState('');

    const [subscriptionInfo, setSubscriptionInfo] = useState(null);
    const [packagePlans, setPackagePlans] = useState(FALLBACK_PACKAGE_PLANS);
    const [subscriptionPlans, setSubscriptionPlans] = useState([]);

    const selectedPackage = useMemo(
        () => packagePlans.find((item) => item.id === selectedPackageId) || packagePlans[0] || null,
        [packagePlans, selectedPackageId]
    );

    const selectedSubscriptionPlan = useMemo(() => pickMonthlyPlan(subscriptionPlans), [subscriptionPlans]);

    const activePlanPayload = useMemo(() => {
        if (activeType === 'subscription') {
            if (!selectedSubscriptionPlan?.id) return null;
            return {
                plan_type: 'subscription',
                plan_id: String(selectedSubscriptionPlan.id),
            };
        }
        if (!selectedPackage?.id) return null;
        return {
            plan_type: 'package',
            plan_id: String(selectedPackage.id),
        };
    }, [activeType, selectedPackage, selectedSubscriptionPlan]);

    const fetchCurrentSubscription = useCallback(async () => {
        if (!authToken) return;
        try {
            const response = await fetch(`${API_BASE_URL}/api/v1/accounts/subscriptions/current/`, {
                headers: { Authorization: `Bearer ${authToken}` },
            });
            if (!response.ok) return;
            const data = await response.json();
            setSubscriptionInfo(data);
        } catch (e) {
            // 非阻断能力
        }
    }, [API_BASE_URL, authToken]);

    const fetchPackagePlans = useCallback(async () => {
        if (!authToken) return;
        try {
            const response = await fetch(`${API_BASE_URL}/api/v1/accounts/packages/plans/`, {
                headers: { Authorization: `Bearer ${authToken}` },
            });
            if (!response.ok) return;
            const data = await response.json();
            const plans = Array.isArray(data) ? data : [];
            setPackagePlans(plans);
            if (plans.length > 0 && !plans.find((item) => item.id === selectedPackageId)) {
                setSelectedPackageId(plans[0].id);
            }
        } catch (e) {
            // 非阻断能力
        }
    }, [API_BASE_URL, authToken, selectedPackageId]);

    const fetchSubscriptionPlans = useCallback(async () => {
        if (!authToken) return;
        try {
            const response = await fetch(`${API_BASE_URL}/api/v1/accounts/subscriptions/plans/`, {
                headers: { Authorization: `Bearer ${authToken}` },
            });
            if (!response.ok) return;
            const data = await response.json();
            setSubscriptionPlans(Array.isArray(data) ? data : []);
        } catch (e) {
            // 非阻断能力
        }
    }, [API_BASE_URL, authToken]);

    const createOrder = useCallback(async () => {
        if (!authToken) {
            setError('请先登录后再支付');
            return;
        }
        if (activeType === 'package' && !activePlanPayload) {
            if (!packagePlans.length) {
                return;
            }
            setError('当前环境未配置可用积分包，请联系管理员配置后重试');
            setQrCodeUrl('');
            setOrderId('');
            setOutTradeNo('');
            return;
        }
        if (activeType === 'subscription' && !activePlanPayload) {
            setError('当前环境未配置可用月订阅计划，请先在后台创建订阅计划');
            setQrCodeUrl('');
            setOrderId('');
            setOutTradeNo('');
            return;
        }

        setLoadingQr(true);
        setPayStatus('pending');
        setQrCodeUrl('');
        setOrderId('');
        setOutTradeNo('');
        setError('');

        try {
            const response = await fetch(`${API_BASE_URL}/api/v1/accounts/billing/orders/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${authToken}`,
                },
                body: JSON.stringify(activePlanPayload),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data?.error || data?.message || '创建订单失败');
            }

            const nextOrderId = data.order_id || data.out_trade_no;
            setOrderId(nextOrderId);
            setOutTradeNo(data.out_trade_no || nextOrderId);
            setQrCodeUrl(toQrImageUrl(data.code_url));
        } catch (e) {
            setError(e.message || '创建订单失败，请稍后重试');
        } finally {
            setLoadingQr(false);
        }
    }, [API_BASE_URL, authToken, activePlanPayload, activeType, packagePlans.length]);

    useEffect(() => {
        fetchCurrentSubscription();
        fetchPackagePlans();
        fetchSubscriptionPlans();
    }, [fetchCurrentSubscription, fetchPackagePlans, fetchSubscriptionPlans]);

    useEffect(() => {
        createOrder();
    }, [createOrder]);

    useEffect(() => {
        if (!outTradeNo || payStatus === 'success') return undefined;

        const timer = setInterval(async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/v1/accounts/billing/orders/${outTradeNo}/status/`, {
                    headers: { Authorization: `Bearer ${authToken}` },
                });
                const data = await res.json();
                if (!res.ok) return;

                if (isPaidStatus(data.status)) {
                    setPayStatus('success');
                    clearInterval(timer);
                    onPaymentSuccess?.(data);
                    fetchCurrentSubscription();
                    setTimeout(() => {
                        onClose?.();
                    }, 1800);
                }
            } catch (e) {
                // 轮询不打断用户
            }
        }, 2000);

        return () => clearInterval(timer);
    }, [API_BASE_URL, authToken, outTradeNo, payStatus, onClose, onPaymentSuccess, fetchCurrentSubscription]);

    const triggerMockSuccess = async () => {
        if (!outTradeNo) return;
        try {
            const res = await fetch(`${API_BASE_URL}/api/v1/accounts/billing/mock-pay/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${authToken}`,
                },
                body: JSON.stringify({ order_id: outTradeNo, out_trade_no: outTradeNo }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(data?.error || '模拟支付失败');
            }
            setPayStatus('success');
            onPaymentSuccess?.(data);
            fetchCurrentSubscription();
            setTimeout(() => {
                onClose?.();
            }, 1800);
        } catch (e) {
            setError(e.message || '模拟支付失败');
        }
    };

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 animate-in fade-in duration-300">
            <div className="bg-white rounded-2xl w-[1050px] shadow-2xl flex overflow-hidden max-h-[90vh] relative">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 z-10 p-2 hover:bg-white/20 bg-black/5 hover:bg-white/40 text-white/80 hover:text-white rounded-full transition-colors"
                >
                    <X className="w-5 h-5" />
                </button>

                <div className="w-[36%] p-6 border-r border-gray-100 bg-white flex flex-col">
                    <div className="flex items-center gap-2 mb-5">
                        <Zap className="w-5 h-5 text-blue-600 fill-current" />
                        <h2 className="text-xl font-bold text-gray-900">充值与订阅</h2>
                    </div>

                    <div className="mb-4 inline-flex rounded-xl bg-gray-100 p-1">
                        <button
                            onClick={() => setActiveType('package')}
                            className={`px-4 py-2 text-sm rounded-lg transition-colors ${activeType === 'package' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-800'}`}
                        >
                            积分包
                        </button>
                        <button
                            onClick={() => setActiveType('subscription')}
                            className={`px-4 py-2 text-sm rounded-lg transition-colors ${activeType === 'subscription' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-800'}`}
                        >
                            月订阅
                        </button>
                    </div>

                    {activeType === 'package' ? (
                        <div className="flex-1 space-y-3 overflow-y-auto p-2">
                            {packagePlans.length > 0 ? (
                                packagePlans.map((plan) => (
                                    <div
                                        key={plan.id}
                                        onClick={() => setSelectedPackageId(plan.id)}
                                        className={`relative border-2 rounded-xl p-4 cursor-pointer transition-all duration-200 ${selectedPackageId === plan.id
                                            ? 'border-blue-600 bg-blue-50/50 shadow-md transform scale-[1.02]'
                                            : 'border-gray-100 hover:border-blue-300 hover:bg-gray-50'
                                            }`}
                                    >
                                        <div className="flex justify-between items-center">
                                            <div>
                                                <div className="text-lg font-bold text-gray-900 flex items-center gap-1">
                                                    {plan.points}
                                                    <span className="text-sm font-normal text-gray-500">点数</span>
                                                </div>
                                                <div className={`text-xs mt-1 ${selectedPackageId === plan.id ? 'text-blue-600 font-medium' : 'text-gray-400'}`}>
                                                    套餐：{plan.name}
                                                </div>
                                            </div>
                                            <div className="text-xl font-bold text-blue-600">
                                                <span className="text-sm align-top mr-0.5">¥</span>
                                                {plan.price}
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="border border-orange-100 bg-orange-50 rounded-xl p-4 text-sm text-orange-600">
                                    暂未检测到可用积分包，请联系管理员在后台配置后重试。
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="flex-1 p-2">
                            {selectedSubscriptionPlan ? (
                                <div className="border-2 border-blue-200 bg-blue-50/60 rounded-xl p-5">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="text-lg font-bold text-gray-900 flex items-center gap-1.5">
                                                <CalendarClock className="w-5 h-5 text-blue-600" />
                                                {selectedSubscriptionPlan.name}
                                            </div>
                                            <p className="text-xs text-blue-700 mt-2 leading-5">{planSubtitle(selectedSubscriptionPlan)}</p>
                                            <p className="text-xs text-blue-500 mt-1">周期：{selectedSubscriptionPlan.cycle_days} 天</p>
                                            <p className="text-xs text-blue-500 mt-1">首期积分：{selectedSubscriptionPlan.points_per_cycle}</p>
                                        </div>
                                        <div className="text-2xl font-bold text-blue-700">
                                            <span className="text-sm align-top mr-0.5">¥</span>
                                            {planPrice(selectedSubscriptionPlan)}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="border border-orange-100 bg-orange-50 rounded-xl p-4 text-sm text-orange-600">
                                    暂未检测到可用订阅计划，请联系管理员在后台配置后重试。
                                </div>
                            )}

                            {subscriptionInfo?.is_active ? (
                                <div className="mt-4 p-3 rounded-xl bg-emerald-50 border border-emerald-100 text-xs text-emerald-700">
                                    <div className="font-semibold flex items-center gap-1.5">
                                        <Crown className="w-3.5 h-3.5" />
                                        当前订阅：{subscriptionInfo.plan_name || '月订阅'}
                                    </div>
                                    <div className="mt-1">剩余天数：{subscriptionInfo.days_left ?? 0} 天</div>
                                </div>
                            ) : (
                                <div className="mt-4 p-3 rounded-xl bg-gray-50 border border-gray-100 text-xs text-gray-600">
                                    当前无有效订阅，开通后立即发放首期积分。
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="w-[28%] bg-gray-50/30 p-4 flex flex-col items-center justify-center text-center border-r border-gray-100 relative">
                    <div className="mb-4 flex items-center text-green-600 font-bold text-sm">
                        <ScanLine className="w-4 h-4 mr-2" /> 微信扫码支付
                    </div>

                    {error && (
                        <div className="mb-3 px-3 py-2 text-xs rounded-lg border border-red-100 bg-red-50 text-red-600 flex items-center gap-1.5">
                            <AlertCircle className="w-3.5 h-3.5" />
                            <span>{error}</span>
                        </div>
                    )}

                    <div className="bg-white p-3 rounded-xl shadow-sm border border-gray-100 relative w-44 h-44 flex items-center justify-center mb-4 transition-all duration-300">
                        {loadingQr ? (
                            <div className="flex flex-col items-center text-gray-400">
                                <Loader2 className="w-6 h-6 animate-spin mb-2" />
                                <span className="text-xs scale-90">请求支付中...</span>
                            </div>
                        ) : payStatus === 'success' ? (
                            <div className="flex flex-col items-center text-green-500 animate-in zoom-in">
                                <CheckCircle2 className="w-12 h-12 mb-2" />
                                <span className="font-bold">支付成功</span>
                            </div>
                        ) : qrCodeUrl ? (
                            <img src={qrCodeUrl} alt="Payment QR" className="w-full h-full object-contain rounded-lg" />
                        ) : (
                            <div className="text-xs text-red-400">二维码加载失败</div>
                        )}

                        {!loadingQr && payStatus !== 'success' && (
                            <div className="absolute -bottom-2.5 bg-green-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold shadow-sm">
                                支付 {activeType === 'subscription' && selectedSubscriptionPlan
                                    ? `¥${planPrice(selectedSubscriptionPlan)}/周期`
                                    : selectedPackage
                                        ? `¥${planPrice(selectedPackage)}`
                                        : '待配置'}
                            </div>
                        )}
                    </div>

                    <p className="text-xs text-gray-400 scale-90">
                        {payStatus === 'success'
                            ? '支付成功，余额已刷新'
                            : activeType === 'subscription'
                                ? '订阅成功后立即发放首期积分'
                                : '支付成功后，点数即刻到账'}
                    </p>
                    {orderId && (
                        <p className="text-[10px] text-gray-300 mt-2">订单号：{orderId}</p>
                    )}

                    {import.meta.env?.DEV && payStatus !== 'success' && outTradeNo && (
                        <button
                            onClick={triggerMockSuccess}
                            className="mt-3 text-[10px] px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-200"
                            title="开发环境模拟支付成功"
                        >
                            模拟支付成功（开发）
                        </button>
                    )}
                </div>

                <div className="w-[36%] bg-[#1e1b4b] text-white p-8 flex flex-col relative overflow-hidden justify-between">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-purple-600/30 rounded-full blur-[80px] -mr-16 -mt-16 pointer-events-none"></div>
                    <div className="absolute bottom-0 left-0 w-56 h-56 bg-blue-600/30 rounded-full blur-[80px] -ml-10 -mb-10 pointer-events-none"></div>

                    <div className="relative z-10 flex flex-col flex-1">
                        <div>
                            <div className="inline-flex items-center bg-yellow-500/20 text-yellow-300 border border-yellow-500/30 px-3 py-1 rounded-full text-[10px] font-bold mb-4 uppercase tracking-wider">
                                <Gem className="w-3 h-3 mr-1.5 fill-current" /> 商业闭环已就绪
                            </div>
                            <h3 className="text-2xl font-bold mb-2 leading-tight">
                                支付成功后
                                <br />
                                积分立即可用
                            </h3>
                            <p className="text-white/60 text-sm mb-6">支持积分包与月订阅两种模式，统一订单轮询确认。</p>
                        </div>

                        <div className="space-y-3.5 mb-auto">
                            <div className="flex items-center gap-3">
                                <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                                <span className="text-sm text-white/90">下单二维码 + 轮询查单</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                                <span className="text-sm text-white/90">支付成功后自动刷新余额</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                                <span className="text-sm text-white/90">月订阅首期积分即时发放</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RechargeModal;
